// 🎨 Whiteboard.js - TOOLBAR IN BASSO
// ✅ Toolbar posizionata IN BASSO, non in alto

import React, { useRef, useEffect, useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const WhiteboardContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #fff;
  z-index: 200;
  display: flex;
  flex-direction: column-reverse; /* ✅ INVERTITO - Toolbar in basso */
  overflow: hidden;
`;

const Toolbar = styled(motion.div)`
  position: sticky;
  bottom: 0; /* ✅ BASSO */
  left: 0;
  right: 0;
  z-index: 300;
  background: linear-gradient(135deg, rgba(26, 26, 46, 0.98), rgba(0, 0, 0, 0.98));
  backdrop-filter: blur(20px);
  border-top: 3px solid rgba(255, 107, 53, 0.5); /* ✅ border-top */
  padding: 1rem 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  box-shadow: 0 -6px 24px rgba(0, 0, 0, 0.4); /* ✅ Ombra verso l'alto */
  
  @media (max-width: 768px) {
    padding: 0.75rem 1rem;
    gap: 0.5rem;
  }
`;

const ToolGroup = styled.div`
  display: flex;
  gap: 0.6rem;
  padding: 0.4rem;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  
  @media (max-width: 768px) {
    gap: 0.4rem;
    padding: 0.3rem;
  }
`;

const ToolButton = styled(motion.button)`
  padding: 0.85rem;
  background: ${props => props.active ? 'linear-gradient(135deg, #FF6B35, #F7931E)' : 'rgba(255, 255, 255, 0.1)'};
  border: 2px solid ${props => props.active ? '#FF6B35' : 'rgba(255, 255, 255, 0.2)'};
  border-radius: 10px;
  color: white;
  cursor: pointer;
  font-size: 1.3rem;
  min-width: 50px;
  min-height: 50px;
  
  &:hover {
    border-color: #FF6B35;
    transform: translateY(-2px);
  }
  
  @media (max-width: 768px) {
    min-width: 45px;
    min-height: 45px;
    font-size: 1.1rem;
  }
`;

const ColorPicker = styled.input`
  width: 50px;
  height: 50px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-radius: 10px;
  cursor: pointer;
  
  &:hover {
    border-color: #FF6B35;
  }
  
  @media (max-width: 768px) {
    width: 45px;
    height: 45px;
  }
`;

const SizeSlider = styled.input`
  width: 140px;
  
  @media (max-width: 768px) {
    width: 100px;
  }
`;

const SizeLabel = styled.span`
  color: white;
  font-weight: 700;
  min-width: 40px;
  text-align: center;
  padding: 0.3rem 0.6rem;
  background: rgba(255, 107, 53, 0.2);
  border-radius: 8px;
`;

const CanvasWrapper = styled.div`
  flex: 1;
  overflow: auto;
  background: #ffffff;
`;

const Canvas = styled.canvas`
  display: block;
  cursor: ${props => props.cursor || 'default'};
  touch-action: none;
  min-width: 100%;
  min-height: 100%;
`;

const ClearButton = styled(motion.button)`
  padding: 0.85rem 1.5rem;
  background: linear-gradient(135deg, #ef4444, #dc2626);
  border: none;
  border-radius: 10px;
  color: white;
  font-weight: 700;
  cursor: pointer;
  margin-left: auto;
  
  @media (max-width: 768px) {
    padding: 0.7rem 1.2rem;
  }
`;

const ActionButton = styled(motion.button)`
  padding: 0.85rem;
  background: rgba(255, 255, 255, 0.1);
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  color: white;
  cursor: pointer;
  font-size: 1.2rem;
  min-width: 50px;
  
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const CloseButton = styled(ActionButton)`
  background: rgba(239, 68, 68, 0.2);
  border-color: rgba(239, 68, 68, 0.4);
`;

const Separator = styled.div`
  width: 2px;
  height: 40px;
  background: rgba(255, 255, 255, 0.2);
  margin: 0 0.3rem;
`;

function Whiteboard({ onClose }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [tool, setTool] = useState('pen');
  const [color, setColor] = useState('#000000');
  const [lineWidth, setLineWidth] = useState(3);
  const [history, setHistory] = useState([]);
  const [historyStep, setHistoryStep] = useState(-1);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const resizeCanvas = () => {
      const wrapper = canvas.parentElement;
      const ctx = canvas.getContext('2d');
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      canvas.width = Math.max(wrapper.clientWidth, 1200);
      canvas.height = Math.max(wrapper.clientHeight, 800);
      ctx.putImageData(imageData, 0, 0);
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    return () => window.removeEventListener('resize', resizeCanvas);
  }, []);

  const saveToHistory = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const newHistory = history.slice(0, historyStep + 1);
    newHistory.push(imageData);
    setHistory(newHistory);
    setHistoryStep(newHistory.length - 1);
  };

  const undo = () => {
    if (historyStep > 0) {
      const ctx = canvasRef.current.getContext('2d');
      ctx.putImageData(history[historyStep - 1], 0, 0);
      setHistoryStep(historyStep - 1);
    }
  };

  const redo = () => {
    if (historyStep < history.length - 1) {
      const ctx = canvasRef.current.getContext('2d');
      ctx.putImageData(history[historyStep + 1], 0, 0);
      setHistoryStep(historyStep + 1);
    }
  };

  const clearCanvas = () => {
    const ctx = canvasRef.current.getContext('2d');
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    saveToHistory();
  };

  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const pos = getCoordinates(e);
    setIsDrawing(true);
    setStartPos(pos);
    const ctx = canvasRef.current.getContext('2d');
    ctx.strokeStyle = tool === 'eraser' ? '#ffffff' : color;
    ctx.lineWidth = tool === 'eraser' ? lineWidth * 3 : lineWidth;
    ctx.lineCap = 'round';
    if (tool === 'pen' || tool === 'eraser') {
      ctx.beginPath();
      ctx.moveTo(pos.x, pos.y);
    }
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const pos = getCoordinates(e);
    const ctx = canvasRef.current.getContext('2d');
    if (tool === 'pen' || tool === 'eraser') {
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
    }
  };

  const stopDrawing = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const pos = getCoordinates(e);
    const ctx = canvasRef.current.getContext('2d');
    if (tool === 'line') {
      ctx.beginPath();
      ctx.moveTo(startPos.x, startPos.y);
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
    } else if (tool === 'rectangle') {
      ctx.strokeRect(startPos.x, startPos.y, pos.x - startPos.x, pos.y - startPos.y);
    } else if (tool === 'circle') {
      const radius = Math.sqrt(Math.pow(pos.x - startPos.x, 2) + Math.pow(pos.y - startPos.y, 2));
      ctx.beginPath();
      ctx.arc(startPos.x, startPos.y, radius, 0, 2 * Math.PI);
      ctx.stroke();
    }
    setIsDrawing(false);
    saveToHistory();
  };

  return (
    <WhiteboardContainer>
      <CanvasWrapper>
        <Canvas
          ref={canvasRef}
          cursor={tool === 'pen' ? 'crosshair' : tool === 'eraser' ? 'cell' : 'default'}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
        />
      </CanvasWrapper>

      <Toolbar initial={{ y: 100 }} animate={{ y: 0 }} transition={{ type: "spring" }}>
        <ToolGroup>
          <ToolButton active={tool === 'pen'} onClick={() => setTool('pen')}>✏️</ToolButton>
          <ToolButton active={tool === 'eraser'} onClick={() => setTool('eraser')}>🧹</ToolButton>
        </ToolGroup>
        <ToolGroup>
          <ToolButton active={tool === 'line'} onClick={() => setTool('line')}>📏</ToolButton>
          <ToolButton active={tool === 'rectangle'} onClick={() => setTool('rectangle')}>▭</ToolButton>
          <ToolButton active={tool === 'circle'} onClick={() => setTool('circle')}>⭕</ToolButton>
        </ToolGroup>
        <Separator />
        <ToolGroup>
          <ColorPicker type="color" value={color} onChange={(e) => setColor(e.target.value)} />
          <SizeLabel>{lineWidth}px</SizeLabel>
          <SizeSlider type="range" min="1" max="30" value={lineWidth} onChange={(e) => setLineWidth(parseInt(e.target.value))} />
        </ToolGroup>
        <Separator />
        <ToolGroup>
          <ActionButton onClick={undo} disabled={historyStep <= 0}>↶</ActionButton>
          <ActionButton onClick={redo} disabled={historyStep >= history.length - 1}>↷</ActionButton>
        </ToolGroup>
        <Separator />
        <ClearButton onClick={clearCanvas}>🗑️ Cancella</ClearButton>
        <CloseButton onClick={onClose}>✕</CloseButton>
      </Toolbar>
    </WhiteboardContainer>
  );
}

export default Whiteboard;
